import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../interceptors";

// ── /candidate/dashboard — branded candidate portal home ───────────────────
// Spec: 4-card grid (applications / action items / recent activity / tip),
// mobile-first, no admin chrome. Backend: POST /job-posting/api/candidate-
// portal/dashboard returns DashboardDTO with the counters + recent
// notifications + recommended resources.

const TIPS = [
  "Make eye contact with the camera, not the screen.",
  "Use the STAR framework for behavioural answers (Situation, Task, Action, Result).",
  "Test your audio in a quiet room before you start.",
  "Aim for clear, structured responses — bullet points in your head help.",
  "Stay within the per-question timer; trim the preamble.",
  "Re-state the question if it helps you organise your thoughts.",
  "It's OK to pause — a beat to think reads better than filler words.",
  "Close every answer with a concrete result or learning.",
  "Practice once in front of your laptop camera before the real run.",
  "Dress like you'd dress for a video call with the team — clean, not formal.",
];

function pickDailyTip() {
  // Stable per day so the tip doesn't change on every render.
  const day = Math.floor(Date.now() / (24 * 60 * 60 * 1000));
  return TIPS[day % TIPS.length];
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function CandidateDashboard() {
  const candidateId = useMemo(() => {
    const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
    return raw ? Number(raw) : null;
  }, []);
  const localName = localStorage.getItem("candidateName") || localStorage.getItem("currentUser") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tip = useMemo(pickDailyTip, []);

  useEffect(() => {
    document.title = "Dashboard - Ample Hire";
    if (!candidateId) {
      setLoading(false);
      setError("Not signed in as a candidate");
      return;
    }
    setLoading(true);
    setError(null);
    axiosInstance
      .post("/job-posting/api/candidate-portal/dashboard", { candidateId })
      .then((r) => {
        if (r.data?.success) setData(r.data.data || {});
        else setError(r.data?.message || "Couldn't load dashboard");
      })
      .catch((e) => setError(e?.message || "Couldn't load dashboard"))
      .finally(() => setLoading(false));
  }, [candidateId]);

  const candidateName = data?.candidateName || localName || "Candidate";
  const stats = {
    pending: data?.interviewsPending ?? 0,
    completed: data?.interviewsCompleted ?? 0,
    total: data?.interviewsTotal ?? 0,
    unread: data?.notificationsUnread ?? 0,
    documents: data?.documentsUploaded ?? 0,
    practice: data?.practiceSessionsCount ?? 0,
    profilePct: data?.profileCompletion ?? 0,
  };
  const notifications = data?.recentNotifications || [];
  const resources = data?.recommendedResources || [];

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* ── Welcome ─────────────────────────────────────────── */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">
            {greeting()}, {candidateName.split(" ")[0]}
          </h1>
          <p className="text-sm text-vf-muted mt-1">
            Track your applications and prep for what's next.
          </p>
        </div>
        {stats.profilePct < 100 && (
          <Link
            to="/candidate/profile"
            className="text-xs px-3 py-1.5 rounded-full bg-vf-tint border border-subtle text-vf-base hover:bg-vf-highlight transition-colors"
          >
            Profile {Math.round(stats.profilePct)}% complete · finish →
          </Link>
        )}
      </header>

      {error && (
        <div role="alert" className="rounded-card border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {/* ── Stat row ─────────────────────────────────────────── */}
      <section
        aria-label="Your activity"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        <StatTile loading={loading} value={stats.total}     label="Interviews" />
        <StatTile loading={loading} value={stats.pending}   label="Pending"     tone="amber" />
        <StatTile loading={loading} value={stats.completed} label="Completed"   tone="emerald" />
        <StatTile loading={loading} value={stats.unread}    label="Unread"      tone="indigo"
                  to="/candidate/notifications" />
      </section>

      {/* ── 2-column on md+, single col on mobile ───────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Action items */}
        <Card title="Action items" titleHint="What needs your attention">
          {loading ? (
            <Placeholder />
          ) : !notifications.some((n) => !n.read) && stats.pending === 0 ? (
            <Empty icon="✅" message="You're all caught up." />
          ) : (
            <ul className="space-y-2.5">
              {stats.pending > 0 && (
                <ActionItem
                  to="/candidate/interviews"
                  icon="🎥"
                  primary={`${stats.pending} pending interview${stats.pending === 1 ? "" : "s"}`}
                  cta="Start"
                />
              )}
              {notifications.filter((n) => !n.read).slice(0, 3).map((n) => (
                <ActionItem
                  key={n.id}
                  to="/candidate/notifications"
                  icon="🔔"
                  primary={n.title || "New notification"}
                  secondary={relTime(n.createdAt)}
                  cta="View"
                />
              ))}
            </ul>
          )}
        </Card>

        {/* Recent activity */}
        <Card title="Recent activity">
          {loading ? (
            <Placeholder />
          ) : notifications.length === 0 ? (
            <Empty icon="📭" message="No activity yet — your interview history will appear here." />
          ) : (
            <ol className="space-y-3 list-none p-0">
              {notifications.slice(0, 5).map((n) => (
                <li key={n.id} className="flex items-start gap-3">
                  <span
                    className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.read ? "bg-slate-300" : "bg-vf-primary"}`}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-vf-base">{n.title || n.message || "Activity"}</div>
                    <div className="text-2xs text-vf-muted mt-0.5">{relTime(n.createdAt)}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* ── Tip of the day ──────────────────────────────────── */}
      <section aria-label="Coaching tip" className="rounded-card bg-vf-tint border border-subtle p-4 flex items-start gap-3">
        <div className="text-xl flex-shrink-0" aria-hidden="true">💡</div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-vf-muted font-semibold mb-1">Tip for your next interview</div>
          <p className="text-sm text-vf-base m-0 leading-relaxed">{tip}</p>
        </div>
      </section>

      {/* ── Recommended prep ────────────────────────────────── */}
      {!loading && resources.length > 0 && (
        <section aria-label="Recommended prep" className="space-y-2">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-vf-base m-0">Recommended prep</h2>
            <Link to="/candidate/resources" className="text-xs text-vf-primary hover:underline">All resources →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {resources.slice(0, 4).map((r) => (
              <article
                key={r.id}
                className="rounded-card border border-subtle bg-white p-3.5 hover:border-vf-primary/40 transition-colors"
              >
                <div className="text-sm font-medium text-vf-base">{r.title}</div>
                {r.description && (
                  <div className="text-xs text-vf-muted mt-1 line-clamp-2">{r.description}</div>
                )}
                {r.category && (
                  <span className="inline-block mt-2 text-2xs uppercase tracking-wide text-vf-muted border border-subtle rounded px-1.5 py-0.5">
                    {r.category}
                  </span>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function StatTile({ value, label, tone = "indigo", to, loading }) {
  const tones = {
    indigo:  "text-vf-primary",
    emerald: "text-emerald-600",
    amber:   "text-amber-600",
  };
  const body = (
    <>
      <div className={`text-2xl font-semibold ${tones[tone] || tones.indigo}`}>
        {loading ? <span className="inline-block w-8 h-7 bg-vf-tint rounded animate-pulse" aria-hidden="true" /> : value}
      </div>
      <div className="text-2xs uppercase tracking-wide text-vf-muted mt-1">{label}</div>
    </>
  );
  const className = "rounded-card border border-subtle bg-white p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary";
  return to ? (
    <Link to={to} className={`${className} hover:border-vf-primary/40 block`}>{body}</Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

function Card({ title, titleHint, children }) {
  return (
    <section aria-label={title} className="rounded-card border border-subtle bg-white p-4">
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-vf-base m-0">{title}</h2>
        {titleHint && <p className="text-2xs text-vf-muted mt-0.5 m-0">{titleHint}</p>}
      </header>
      {children}
    </section>
  );
}

function Placeholder() {
  return (
    <div className="space-y-2" aria-busy="true">
      <div className="h-4 bg-vf-tint rounded animate-pulse w-3/4" />
      <div className="h-4 bg-vf-tint rounded animate-pulse w-1/2" />
      <div className="h-4 bg-vf-tint rounded animate-pulse w-2/3" />
    </div>
  );
}

function Empty({ icon, message }) {
  return (
    <div className="text-center py-6 px-2">
      <div className="text-2xl mb-1.5" aria-hidden="true">{icon}</div>
      <p className="text-xs text-vf-muted m-0">{message}</p>
    </div>
  );
}

function ActionItem({ to, icon, primary, secondary, cta }) {
  return (
    <li className="flex items-center gap-3 rounded-md border border-subtle p-3 hover:border-vf-primary/40 transition-colors">
      <span className="text-base flex-shrink-0" aria-hidden="true">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-vf-base truncate">{primary}</div>
        {secondary && <div className="text-2xs text-vf-muted">{secondary}</div>}
      </div>
      <Link
        to={to}
        className="text-xs font-semibold text-vf-primary hover:underline px-2 py-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
      >
        {cta} →
      </Link>
    </li>
  );
}
